import { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Cropper from "react-easy-crop";
import Toast from "./Toast";
import { IoSearchOutline, IoReloadOutline, IoCheckmark } from "react-icons/io5";
import { lockBodyScroll } from "../utils/scrollLock";
import { useFocusTrap } from "../utils/focusTrap";

// "Original" keeps the photo's own shape (no cropping to a preset), so a photo
// of any size can be posted as it is; the rest are common post shapes, from
// tall (9:16) to wide (16:9).
const MIN_ASPECT = 9 / 16;
const MAX_ASPECT = 16 / 9;

const ASPECT_OPTIONS = [
  { label: "Original", value: "original" },
  { label: "1:1", value: 1 },
  { label: "4:5", value: 4 / 5 },
  { label: "3:4", value: 3 / 4 },
  { label: "9:16", value: 9 / 16 },
  { label: "4:3", value: 4 / 3 },
  { label: "16:9", value: 16 / 9 },
];

// The shape among the choices that is closest to `wanted` (1:1 if there is none).
function nearestAspect(wanted) {
  if (!wanted) return 1;
  const shapes = ASPECT_OPTIONS.map(o => o.value).filter(v => typeof v === "number");
  return shapes.reduce((best, v) => (Math.abs(Math.log(v / wanted)) < Math.abs(Math.log(best / wanted)) ? v : best), 1);
}

// `initialAspect`: the shape to start on (a post's later photos start on the shape
// of its first one, so they sit together in one frame).
export default function ImageCropModal({ imageSrc, fileName, lockAspect, initialAspect, onCancel, onSave }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState(() => nearestAspect(initialAspect));
  // The photo's own width/height, known once it has loaded (for "Original").
  const [naturalAspect, setNaturalAspect] = useState(1);
  const aspectRowRef = useRef(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const savingRef = useRef(false);
  const modalRef = useRef(null);
  useFocusTrap(modalRef, true);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  // Every other modal in the app (ReportModal, FollowListModal) closes on
  // Escape — this one was missing it. Guarded by `saving` the same way the
  // Cancel button is, so it can't abandon an export that's already running.
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape" && !saving) onCancel?.();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel, saving]);

  // "Original" follows the photo's shape, turned on its side when rotated 90° —
  // but only within the tallest (9:16) and widest (16:9) shapes offered here, so
  // something like a very long screenshot can't become a post several screens
  // tall (it is trimmed to the nearest allowed shape instead).
  const ownAspect = rotation % 180 === 0 ? naturalAspect : 1 / naturalAspect;
  const effectiveAspect = lockAspect
    ? 1
    : aspect === "original"
      ? Math.min(MAX_ASPECT, Math.max(MIN_ASPECT, ownAspect))
      : aspect;

  // The choices scroll sideways; keep the picked one in view.
  useEffect(() => {
    aspectRowRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ inline: "nearest", block: "nearest" });
  }, [aspect]);

  // Lock the page behind while cropping, like every other overlay.
  useEffect(() => lockBodyScroll(), []);

  async function handleSave() {
    if (!croppedAreaPixels) return;
    // disabled={saving} only takes effect on the next render — a very fast
    // double-click can fire this twice before that paints, which would
    // otherwise add the same photo to the post twice.
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, rotation);
      const file = new File([blob], fileName || "photo.jpg", { type: "image/jpeg" });
      // The cropped shape goes along, for callers that want to reuse it.
      onSave(file, croppedAreaPixels.width / croppedAreaPixels.height);
    } catch (err) {
      console.log("Crop failed:", err);
      setToast("Couldn't process that photo. Try again.");
      savingRef.current = false;
      setSaving(false);
    }
  }

  // Portaled for consistency with every other overlay in the app (and the
  // same reason as Toast.jsx/ShareButton.jsx/ImageLightbox.jsx) — this one's
  // trigger points (Upload.jsx, Profile.jsx's avatar crop) are shallower so
  // the risk is lower, but there's no reason to leave it as the exception.
  return createPortal(
    <div style={backdrop}>
      <style>{css}</style>
      <div style={modal} ref={modalRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={lockAspect ? "Profile photo" : "Crop photo"}>
        <div style={header}>
          <div style={title}>{lockAspect ? "Profile photo" : "Crop photo"}</div>
          <div style={hint}>Drag to move · pinch or slide to zoom</div>
        </div>

        <div style={cropArea}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={effectiveAspect}
            cropShape={lockAspect ? "round" : "rect"}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            onMediaLoaded={media => {
              if (media.naturalWidth && media.naturalHeight) {
                setNaturalAspect(media.naturalWidth / media.naturalHeight);
              }
            }}
          />
        </div>

        <div style={controls}>
          {!lockAspect && (
            <div className="virsa-aspect-wrap">
              <div style={aspectRow} ref={aspectRowRef} className="virsa-aspect-row">
                {ASPECT_OPTIONS.map(opt => (
                  <button
                    key={opt.label}
                    data-active={aspect === opt.value ? "true" : undefined}
                    aria-pressed={aspect === opt.value}
                    style={{
                      ...aspectBtn,
                      ...(aspect === opt.value ? aspectBtnActive : {}),
                    }}
                    onClick={() => setAspect(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={toolRow}>
            <IoSearchOutline style={toolIcon} aria-hidden="true" />
            <input
              type="range"
              className="virsa-zoom"
              aria-label="Zoom"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={e => setZoom(Number(e.target.value))}
            />
            <button
              style={roundBtn}
              onClick={() => setRotation(r => (r - 90 + 360) % 360)}
              aria-label="Rotate left"
              title="Rotate left"
            >
              <IoReloadOutline style={{ transform: "scaleX(-1)" }} />
            </button>
            <button
              style={roundBtn}
              onClick={() => setRotation(r => (r + 90) % 360)}
              aria-label="Rotate right"
              title="Rotate right"
            >
              <IoReloadOutline />
            </button>
          </div>

          <div style={actionRow}>
            <button style={cancelBtn} onClick={onCancel} disabled={saving}>
              Cancel
            </button>
            <button
              style={{ ...doneBtn, opacity: croppedAreaPixels ? 1 : 0.6 }}
              onClick={handleSave}
              disabled={saving || !croppedAreaPixels}
            >
              {saving ? "Applying…" : (<><IoCheckmark style={{ verticalAlign: -2, marginRight: 4 }} />Done</>)}
            </button>
          </div>
        </div>
      </div>

      {toast && <Toast text={toast} onClose={() => setToast("")} />}
    </div>,
    document.body
  );
}

/* ===== Canvas helpers: crop + rotate the source image into a JPEG blob ===== */

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// A cropped phone photo can still be 3000px+ on a side — far more than any
// screen will ever display it at in this app's feed/lightbox — so anything
// past this gets scaled down before export. Cuts file size (and therefore
// both Storage and bandwidth) by several times with no visible quality
// loss at normal viewing sizes.
const MAX_DIMENSION = 1440;
const JPEG_QUALITY = 0.82;

async function getCroppedBlob(imageSrc, croppedAreaPixels, rotationDeg) {
  const image = await loadImage(imageSrc);
  const rotationRad = (rotationDeg * Math.PI) / 180;

  // Draw the full rotated image onto a big enough canvas first
  const rotatedSize = getRotatedSize(image.width, image.height, rotationRad);

  const rotateCanvas = document.createElement("canvas");
  rotateCanvas.width = rotatedSize.width;
  rotateCanvas.height = rotatedSize.height;
  const rotateCtx = rotateCanvas.getContext("2d");

  rotateCtx.translate(rotatedSize.width / 2, rotatedSize.height / 2);
  rotateCtx.rotate(rotationRad);
  rotateCtx.drawImage(image, -image.width / 2, -image.height / 2);

  // Now crop the requested pixel area out of the rotated canvas
  const cropCanvas = document.createElement("canvas");
  cropCanvas.width = croppedAreaPixels.width;
  cropCanvas.height = croppedAreaPixels.height;
  const cropCtx = cropCanvas.getContext("2d");

  cropCtx.drawImage(
    rotateCanvas,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    croppedAreaPixels.width,
    croppedAreaPixels.height
  );

  // Downscale to the capped resolution (a no-op if it's already smaller).
  const scale = Math.min(1, MAX_DIMENSION / Math.max(cropCanvas.width, cropCanvas.height));
  const outCanvas = document.createElement("canvas");
  outCanvas.width = Math.round(cropCanvas.width * scale);
  outCanvas.height = Math.round(cropCanvas.height * scale);
  const outCtx = outCanvas.getContext("2d");

  // JPEG has no alpha channel — a transparent source (a PNG mockup/graphic
  // with a transparent background, say) would otherwise composite onto an
  // undrawn canvas as black once exported, instead of staying white.
  outCtx.fillStyle = "#fff";
  outCtx.fillRect(0, 0, outCanvas.width, outCanvas.height);
  outCtx.drawImage(cropCanvas, 0, 0, outCanvas.width, outCanvas.height);

  return new Promise((resolve, reject) => {
    outCanvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error("Canvas export failed"))),
      "image/jpeg",
      JPEG_QUALITY
    );
  });
}

function getRotatedSize(width, height, rotationRad) {
  const cos = Math.abs(Math.cos(rotationRad));
  const sin = Math.abs(Math.sin(rotationRad));
  return {
    width: Math.round(width * cos + height * sin),
    height: Math.round(width * sin + height * cos),
  };
}

/* ===== STYLES (Virsa palette) ===== */

const backdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(20, 14, 8, 0.72)",
  zIndex: 1200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 12,
};

const modal = {
  // Follows the theme like the other popups (a fixed cream background made the
  // title all but invisible in dark mode).
  background: "var(--bg-surface)",
  borderRadius: 20,
  width: "100%",
  maxWidth: 440,
  maxHeight: "100%",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 20px 50px rgba(0, 0, 0, 0.4)",
};

const header = { padding: "14px 18px 12px", flexShrink: 0 };
const title = { fontSize: 16, fontWeight: 800, color: "var(--text-heading)" };
const hint = { marginTop: 2, fontSize: 12, color: "var(--text-muted)" };

// Takes the room the controls leave (up to 400px), so the photo is as big as
// the screen allows instead of a fixed 320px strip.
const cropArea = {
  position: "relative",
  width: "100%",
  height: "min(50vh, 400px)",
  minHeight: 200,
  flex: "1 1 auto",
  background: "#111",
};

const controls = { padding: "14px 16px 16px", flexShrink: 0 };

// One line that scrolls sideways — seven choices don't fit side by side. The
// fade on its right edge (see css) hints that there are more.
const aspectRow = {
  display: "flex",
  gap: 8,
  overflowX: "auto",
  padding: "2px 16px",
  scrollbarWidth: "none",
  WebkitOverflowScrolling: "touch"
};

const aspectBtn = {
  flex: "0 0 auto",
  padding: "8px 16px",
  borderRadius: 999,
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-surface)",
  color: "var(--text-secondary)",
  fontWeight: 700,
  fontSize: 13,
  cursor: "pointer",
};

const aspectBtnActive = {
  background: "var(--accent-primary)",
  border: "1.5px solid var(--accent-primary)",
  color: "var(--accent-contrast)",
};

// Zoom slider with the two rotate buttons on the same line.
const toolRow = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 14,
};

const toolIcon = { flexShrink: 0, fontSize: 20, color: "var(--text-muted)" };

const roundBtn = {
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: "50%",
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-surface)",
  color: "var(--text-secondary)",
  fontSize: 19,
  cursor: "pointer",
};

const actionRow = { display: "flex", gap: 10 };

const cancelBtn = {
  flex: 1,
  height: 48,
  boxSizing: "border-box",
  borderRadius: 14,
  border: "1.5px solid var(--border-input)",
  background: "none",
  color: "var(--text-secondary)",
  fontWeight: 700,
  fontSize: 15,
  cursor: "pointer",
};

const doneBtn = {
  flex: 2,
  height: 48,
  boxSizing: "border-box",
  borderRadius: 14,
  border: "1.5px solid var(--accent-primary)",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontWeight: 700,
  fontSize: 15,
  cursor: "pointer",
};

const css = `
  .virsa-aspect-wrap { position: relative; margin: 0 -16px 14px; }
  .virsa-aspect-row::-webkit-scrollbar { display: none; }
  .virsa-aspect-wrap::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 32px;
    pointer-events: none;
    background: linear-gradient(to right, transparent, var(--bg-surface));
  }
  .virsa-zoom { flex: 1; min-width: 0; height: 28px; accent-color: var(--accent-primary); cursor: pointer; }
`;
